import type { ReactNode } from 'react';

/** Plain grouped-list surface (an inset card). Rows/spec cells live inside. */
export function Card({
  children,
  className = '',
  padded = false,
}: {
  children: ReactNode;
  className?: string;
  padded?: boolean;
}) {
  return (
    <div
      className={`overflow-hidden rounded-[14px] border border-hair bg-elev ${padded ? 'px-4 py-1' : ''} ${className}`}
    >
      {children}
    </div>
  );
}

/** Section heading above a grouped card: an eyebrow (plain capitals), with optional trailing actions. */
export function GroupLabel({
  children,
  actions,
}: {
  children: ReactNode;
  actions?: ReactNode;
}) {
  return (
    <div className="mb-2.5 ml-1 mt-7 flex min-h-6 items-center gap-2">
      <h2 className="eyebrow">{children}</h2>
      {actions && <div className="ml-auto flex items-center gap-2">{actions}</div>}
    </div>
  );
}

/** Label / value row for grouped lists. */
export function Row({
  label,
  value,
  mono = false,
  children,
}: {
  label: ReactNode;
  value?: ReactNode;
  mono?: boolean;
  children?: ReactNode;
}) {
  return (
    <div className="flex min-h-12 flex-col gap-0.5 border-t border-rowhair px-4 py-3 first:border-t-0 sm:flex-row sm:items-center sm:gap-3">
      <span className="text-[15px] text-ink2 sm:shrink-0">{label}</span>
      <span className={`min-w-0 flex-1 break-words text-[15px] text-ink sm:text-right ${mono ? 'font-mono' : ''}`}>
        {value ?? children ?? '—'}
      </span>
    </div>
  );
}

/** Titled section with a padded body — for generic content and forms. */
export function Section({
  title,
  description,
  actions,
  children,
  className = '',
  bodyClassName = 'p-4',
}: {
  title?: ReactNode;
  description?: ReactNode;
  actions?: ReactNode;
  children: ReactNode;
  className?: string;
  bodyClassName?: string;
}) {
  return (
    <section className={className}>
      {(title || actions) && <GroupLabel actions={actions}>{title}</GroupLabel>}
      {description && <p className="mb-2 ml-1 -mt-1 text-sm text-ink2">{description}</p>}
      <div className={`overflow-hidden rounded-[14px] border border-hair bg-elev ${bodyClassName}`}>
        {children}
      </div>
    </section>
  );
}
